<script setup lang="ts">
import { omit } from 'lodash-es';
import {
  computed,
  ref,
} from 'vue';
import { useRouter } from 'vue-router';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IRegistrationFields, IRegistrationPayload } from '@modules/registration/types';
import { useConfig } from '@modules/config/composables';
import VFormCheckbox from '@modules/forms/components/inputs/VFormCheckbox/VFormCheckbox.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import {
  useI18n,
  useLanguages,
  useLocale,
} from '@modules/i18n/composables';
import {
  REGISTRATION_CONFIRMATION_ROUTE_NAME,
  TRANSLATION_GROUP_REGISTRATION,
} from '@modules/registration/constants';
import { useRegisterMutation } from '@modules/registration/mutations';
import { RESPONSE_DATE_FORMAT } from '@modules/smart-filters/constants';
import StaticPageDialogButton from '@modules/static-pages/components/StaticPageDialogButton/StaticPageDialogButton.vue';
import { EStaticPageName } from '@modules/static-pages/enums';
import { assert, isDate, isString } from '@utils/guards';
import { formatDate } from '@vueuse/core';

const { getTranslation } = useI18n();
const { interfaceLanguages } = useLanguages();
const { currentLocale } = useLocale();
const { config } = useConfig();
const router = useRouter();

const languageOfCommunication = ref<string>();

const allRegistrationFields = computed<FormSchema<IRegistrationFields>>(() => ({
  name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.NAME').value,
  },
  surname: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.SURNAME').value,
  },
  username: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.USERNAME').value,
  },
  email: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.EMAIL').value,
  },
  password: {
    type: EFormInputTypes.PASSWORD,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.PASSWORD').value,
  },
  phone_number: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.PHONE_NUMBER').value,
  },
  ms_teams: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.MS_TEAMS').value,
  },
  telegram: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.TELEGRAM').value,
  },
  whatsapp: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.WHATSAPP').value,
  },
  birthday: {
    type: EFormInputTypes.DATE,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.BIRTHDAY').value,
    maxDate: new Date(),
  },
  site_url: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.SITE_URL').value,
  },
  language_of_communication: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.LANGUAGE_OF_COMMUNICATION').value,
    options: interfaceLanguages.value,
    value: languageOfCommunication.value || currentLocale.value,
  },
  about_affiliate: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.ABOUT_AFFILIATE').value,
  },
  accept_terms_and_conditions: {
    type: EFormInputTypes.SLOT,
    required: true,
  },
  agree_receive_emails: {
    type: EFormInputTypes.CHECKBOX,
    label: getTranslation(TRANSLATION_GROUP_REGISTRATION, 'FORM.AGREE_RECEIVE_EMAILS').value,
  },
}));

const requiredRegistrationFields = computed<string[]>(() => (
  [
    ...(config.value?.required_fields_in_profile_update_affiliate.filter(({ check }) => check).map(({ id }) => id) ?? []),
    ...(config.value?.auto_accept_affiliate_request ? ['language_of_communication'] : []),
    'agree_receive_emails',
    'accept_terms_and_conditions',
    'email',
    'password',
  ]
));

const filteredRegistrationSchema = computed<FormSchema<IRegistrationFields>>(() => {
  const schema: FormSchema<IRegistrationFields> = {};

  for (const [key, entry] of Object.entries(allRegistrationFields.value)) {
    if (requiredRegistrationFields.value.includes(key)) {
      Object.assign(schema, {
        [key]: entry,
      });
    }
  }

  return schema;
});

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
} = useRegisterMutation();
const isDisabled = ref(true);

function onFormChange<
  T extends IRegistrationFields,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
): void {
  if (key === 'accept_terms_and_conditions') {
    isDisabled.value = !value;
  }
  if (key === 'language_of_communication') {
    assert(isString(value));
    languageOfCommunication.value = value;
  }
}

async function onSubmit(values: IRegistrationFields): Promise<void> {
  const payload: IRegistrationPayload = omit(values, ['birthday']);

  if (isDate(values.birthday)) {
    payload.birthday = formatDate(values.birthday, RESPONSE_DATE_FORMAT);
  }
  await mutateAsync(payload);
  void router.replace({
    name: REGISTRATION_CONFIRMATION_ROUTE_NAME,
  });
}
</script>

<template>
  <div v-data-qa-auto-id="'RegistrationPage'">
    <div class="mb-8 text-center text-3xl font-light uppercase">
      {{ $getTranslation(TRANSLATION_GROUP_REGISTRATION, 'HEADER') }}
    </div>
    <VForm
      :schema="filteredRegistrationSchema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_REGISTRATION, 'SUBMIT_BTN')"
      is-buttons-centered
      :is-loading="isLoading"
      :disabled="isDisabled"
      :custom-errors="errors"
      @interacted="clearError"
      @change="onFormChange"
      @submit="onSubmit"
    >
      <template #accept_terms_and_conditions="{ className, onChange, onInput, errors: inputErrors }">
        <VFormCheckbox
          :class="className"
          :errors="inputErrors"
          @change="onChange('accept_terms_and_conditions', $event)"
          @input="onInput('accept_terms_and_conditions', $event)"
        >
          <template #label>
            <div>
              {{ $getTranslation(TRANSLATION_GROUP_REGISTRATION, 'ATTENTION_FIRST_PART') }}
              <StaticPageDialogButton
                :page="EStaticPageName.PRIVACY"
              />&nbsp;
              <StaticPageDialogButton
                :page="EStaticPageName.TERMS"
              />
            </div>
          </template>
        </VFormCheckbox>
      </template>
    </VForm>
  </div>
</template>
